/*
 * Marketplace specific configuration.
 *
 * Every filter needs to have following keys:
 * - id:     Unique id of the filter.
 * - label:  The default label of the filter.
 * - type:   String that represents one of the existing filter components:
 *           BookingDateRangeFilter, KeywordFilter, PriceFilter,
 *           SelectSingleFilter, SelectMultipleFilter.
 * - group:  Is this 'primary' or 'secondary' filter?
 *           Primary filters are visible on desktop layout by default.
 *           Secondary filters are behind "More filters" button.
 *           Read more from src/containers/SearchPage/README.md
 * - queryParamNames: Describes parameters to be used with queries
 *                    (e.g. 'price' or 'pub_amenities'). Most of these are
 *                    the same between webapp URLs and API query params.
 *                    You can't change 'dates', 'price', or 'keywords'
 *                    since those filters are fixed to a specific attribute.
 * - config: Extra configuration that the filter component needs.
 *
 * Note 1: Labels could be tied to translation file
 *         by importing FormattedMessage:
 *         <FormattedMessage id="some.translation.key.here" />
 *
 * Note 2: If you need to add new custom filter components,
 *         you need to take those into use in:
 *         src/containers/SearchPage/FilterComponent.js
 *
 * Note 3: If you just want to create more enum filters
 *         (i.e. SelectSingleFilter, SelectMultipleFilter),
 *         you can just add more configurations with those filter types
 *         and tie them with correct extended data key
 *         (i.e. pub_<key> or meta_<key>).
 */

import { DAY, FIXED_DISCOUNT, MONTH, PERCENTAGE, WEEK } from './util/types';
import {
  INBOX_SORT_BY_LATEST_CONTACT,
  INBOX_SORT_BY_BOOKING_START,
  INBOX_SORT_BY_RETURN_DATE,
  INBOX_FILTER_ENQUIRY_ONLY,
  INBOX_FILTER_NOT_YET_COMMENCED,
  INBOX_FILTER_CURRENT_RENTALS,
  INBOX_FILTER_ENDED_RENTALS,
} from './util/types';

export const filters = [
  {
    id: 'dates',
    label: 'Dates',
    type: 'BookingDateRangeFilter',
    group: 'primary',
    // Note: BookingDateRangeFilter is fixed filter,
    // you can't change "queryParamNames: ['dates'],"
    queryParamNames: ['dates'],
    config: {},
  },
  {
    id: 'keyword',
    label: 'Keyword',
    type: 'KeywordFilter',
    group: 'primary',
    // Note: KeywordFilter is fixed filter,
    // you can't change "queryParamNames: ['keywords'],"
    queryParamNames: ['keywords'],
    // NOTE: If you are ordering search results by distance
    // the keyword search can't be used at the same time.
    // You can turn on/off ordering by distance from config.js file.
    config: {},
  },
  {
    id: 'category',
    label: 'Category',
    type: 'SelectSingleFilter',
    group: 'primary',
    queryParamNames: ['pub_category'],
    config: {
      childrenQueryName: 'pub_subCategory',
      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        { key: 'carSeats', labelId: 'Config.categories.carSeats' },
        { key: 'cotsAndBassinets', labelId: 'Config.categories.cotsAndBassinets' },
        { key: 'pramsStrollers', labelId: 'Config.categories.pramsStrollers' },
        { key: 'newborn', labelId: 'Config.categories.newborn' },
        { key: 'breastPumps', labelId: 'Config.categories.breastPumps' },
        { key: 'travelAccessories', labelId: 'Config.categories.travelAccessories' },
        { key: 'pramAndCarSeatBags', labelId: 'Config.categories.pramAndCarSeatBags' },
        { key: 'snowAndWaterGear', labelId: 'Config.categories.snowAndWaterGear' },
        { key: 'parties', labelId: 'Config.categories.parties' },
        { key: 'toys', labelId: 'Config.categories.toys' },
        { key: 'maternity', labelId: 'Config.categories.maternity' },
      ],
    },
  },
  {
    id: 'subCategory',
    label: 'Sub category',
    type: 'SelectSingleFilter',
    group: 'primary',
    queryParamNames: ['pub_subCategory'],
    config: {
      parentFilter: 'category',
      parentQueryName: 'pub_category',
      // "key" is the option you see in Flex Console.
      // "label" is set here for the UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      optionsObj: {
        carSeats: [
          {
            key: 'newbornCapsules',
            labelId: 'Config.subcategories.newbornCapsules',
          },
          {
            key: 'carSeats',
            labelId: 'Config.subcategories.carSeats',
          },
        ],
        cotsAndBassinets: [
          {
            key: 'travelCots',
            labelId: 'Config.subcategories.travelCots',
          },
          {
            key: 'rockingBassinets',
            labelId: 'Config.subcategories.rockingBassinets',
          },
          {
            key: 'bassinets',
            labelId: 'Config.subcategories.bassinets',
          },
          {
            key: 'fullSizedCots',
            labelId: 'Config.subcategories.fullSizedCots',
          },
        ],
        pramsStrollers: [
          {
            key: 'compactPrams',
            labelId: 'Config.subcategories.compactPrams',
          },
          {
            key: 'fullSizedPrams',
            labelId: 'Config.subcategories.fullSizedPrams',
          },
        ],
        newborn: [
          {
            key: 'babyBouncers',
            labelId: 'Config.subcategories.babyBouncers',
          },
          {
            key: 'babyMonitors',
            labelId: 'Config.subcategories.babyMonitors',
          },
        ],
        breastPumps: [
          {
            key: 'breastPumps',
            labelId: 'Config.subcategories.breastPumps',
          },
        ],
        travelAccessories: [
          {
            key: 'planeCushions',
            labelId: 'Config.subcategories.planeCushions',
          },
          {
            key: 'CARESHarnesses',
            labelId: 'Config.subcategories.CARESHarnesses',
          },
          {
            key: 'travelHighChairs',
            labelId: 'Config.subcategories.travelHighChairs',
          },
          {
            key: 'carriers',
            labelId: 'Config.subcategories.carriers',
          }
        ],
        pramAndCarSeatBags: [
          {
            key: 'pramAndCarSeatBags',
            labelId: 'Config.subcategories.pramAndCarSeatBags',
          },
        ],
        snowAndWaterGear: [
          {
            key: 'snowAndWaterGear',
            labelId: 'Config.subcategories.snowAndWaterGear',
          },
        ],
        parties: [
          {
            key: 'parties',
            labelId: 'Config.subcategories.parties',
          },
        ],
        toys: [
          {
            key: 'toys',
            labelId: 'Config.subcategories.toys',
          },
        ],
        maternity: [
          {
            key: 'maternity',
            labelId: 'Config.subcategories.maternity',
          },
        ],
      },
    },
  },
  {
    id: 'deliveryTypes',
    label: 'Delivery Types',
    type: 'SelectMultipleFilter',
    group: 'secondary',
    queryParamNames: ['pub_deliveryType'],
    config: {
      // Optional modes: 'has_all', 'has_any'
      // https://www.sharetribe.com/api-reference/marketplace.html#extended-data-filtering
      searchMode: 'has_any',

      // "key" is the option you see in Flex Console.
      // "label" is set here for this web app's UI only.
      // Note: label is not added through the translation files
      // to make filter customizations a bit easier.
      options: [
        {
          key: 'pickup',
          label: 'Pickup',
        },
        {
          key: 'delivery',
          label: 'Delivery',
        },
      ],
    },
  },
  {
    id: 'distance',
    label: 'Distance',
    type: 'SingleRangeFilter',
    group: 'primary',
    queryParamNames: ['distance'],
    config: {
      min: 1,
      max: 100,
      step: 1,
    },
  },
];

export const sortConfig = {
  // Enable/disable the sorting control in the SearchPage
  active: true,

  // Note: queryParamName 'sort' is fixed,
  // you can't change it since Flex API expects it to be named as 'sort'
  queryParamName: 'sort',

  // Internal key for the relevance option, see notes below.
  relevanceKey: 'relevance',

  // Keyword filter is sorting the results already by relevance.
  // If keyword filter is active, we need to disable sorting.
  conflictingFilters: ['keyword'],

  options: [
    { key: 'createdAt', label: 'Newest' },
    { key: '-createdAt', label: 'Oldest' },
    { key: '-price', label: 'Lowest price' },
    { key: 'price', label: 'Highest price' },

    // The relevance is only used for keyword search, but the
    // parameter isn't sent to the Marketplace API. The key is purely
    // for handling the internal state of the sorting dropdown.
    { key: 'relevance', label: 'Relevance', longLabel: 'Relevance (Keyword search)' },
  ],
};

export const priceDurationConfig = [
  {
    key: DAY,
    labelId: 'Config.priceDuration.day',
    labelListingId: 'Config.priceDuration.perDay'
  },
  {
    key: WEEK,
    labelId: 'Config.priceDuration.week',
    labelListingId: 'Config.priceDuration.perWeek'
  },
  {
    key: MONTH,
    labelId: 'Config.priceDuration.month',
    labelListingId: 'Config.priceDuration.perMonth'
  },
];

export const cancellationConfig = [
  {
    key: 'noRefund',
    labelId: 'Config.cancellation.noRefund',
    descriptionId: 'Config.cancellation.noRefund.description',
  },
  {
    key: 'fullRefund',
    labelId: 'Config.cancellation.fullRefund',
    descriptionId: 'Config.cancellation.fullRefund.description',
  },
];

export const typesOfDiscount = [
  {
    key: FIXED_DISCOUNT, labelId: 'Config.typesOfDiscount.fixedDiscount'
  },
  {
    key: PERCENTAGE, labelId: 'Config.typesOfDiscount.percentage'
  }
];

export const typesOfDuration = [
  {
    key: DAY, labelId: 'Config.typesOfDiscount.day'
  },
  {
    key: WEEK, labelId: 'Config.typesOfDiscount.week'
  },
  {
    key: MONTH, labelId: 'Config.typesOfDiscount.month'
  },
];

export const ONE_WEEK = 7;
export const ONE_MONTH = 30;

export const queryTypeContactUsConfig = [
  {
    key: 'helpMeFindWhatINeed',
    labelId: 'Config.queryType.helpMeFindWhatINeed',
  },
  {
    key: 'ownerSupport',
    labelId: 'Config.queryType.ownerSupport',
  },
  {
    key: 'renterSupport',
    labelId: 'Config.queryType.renterSupport',
  },
  {
    key: 'generalEnquiry',
    labelId: 'Config.queryType.generalEnquiry',
  },
];

export const inboxFilters = [
  {
    id: 'state',
    label: 'Filter',
    type: 'SelectSingleFilter',
    group: 'primary',
    queryParamNames: ['tx_state'],
    config: {
      options: [
        { key: INBOX_FILTER_ENQUIRY_ONLY, labelId: 'InboxConfig.state.enquiryOnly' },
        { key: INBOX_FILTER_NOT_YET_COMMENCED, labelId: 'InboxConfig.state.notYetCommenced' },
        { key: INBOX_FILTER_CURRENT_RENTALS, labelId: 'InboxConfig.state.currentRentals' },
        { key: INBOX_FILTER_ENDED_RENTALS, labelId: 'InboxConfig.state.endedRentals' },
      ],
    },
  },
];

export const inboxSortConfig = {
  // Enable/disable the sorting control in the InboxPage
  active: true,

  // Note: queryParamName 'sort' is fixed,
  // you can't change it since Flex API expects it to be named as 'sort'
  queryParamName: 'sort',

  relevanceKey: '',

  conflictingFilters: [],

  options: [
    { key: INBOX_SORT_BY_BOOKING_START, label: 'Latest Booking Start Dates' },
    { key: INBOX_SORT_BY_RETURN_DATE, label: 'Latest Item Returned Dates' },
    { key: INBOX_SORT_BY_LATEST_CONTACT, label: 'Latest Contact' },
  ],
};
